import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuGroup,
  MenuItem,
  MenuList,
  Skeleton,
  Text
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { NavLink, useLocation } from 'react-router-dom';
import React, { useState } from 'react';
import useAuthAccessQuery from 'src/hooks/useAuthAccessQuery';
import { faSliders } from '@fortawesome/pro-regular-svg-icons';
import { ButtonText } from '@morressier/alembic';
import { SubmissionSeeder } from '../SubmissionSeeder';
import KPIRedirectModal from '../KPIRedirectModal';
import { IWorkspaceFeatureConfig } from '@publishing-engine/domain/models';

interface Props {
  workspace: {
    workspaceId: string;
    shortname: string;
    name: string;
    featureConfig?: IWorkspaceFeatureConfig;
  };
}

export const WorkspaceNavLoader = ({ size }: { size: 'normal' | 'large' }) => {
  const { height, width } =
    size === 'large'
      ? { height: 10, width: '2.5rem' }
      : { height: 6, width: '1.5rem' };

  return (
    <Flex gap={2} alignItems={'center'}>
      <Skeleton height={height} width={width} />
      <Skeleton height={6} flex={1}>
        <Text textStyle="headingGroup">Organization Name</Text>
      </Skeleton>
    </Flex>
  );
};

const getAppEnv = (realm: string, env: string) => {
  if(realm === 'm'){
    return 'm'
  }
  if (env === 'staging') {
    return `${realm}-staging`;
  }
  if (env === 'preproduction') {
    return `${realm}-preprod`;
  }
  if (env === 'production') {
    return `${realm}`;
  }
};
export const ManageWorkspace: React.FC<Props> = ({ workspace }) => {
  const KPI_URL = import.meta.env.VITE_KPI_URL;
  const CLIENT_REALM = import.meta.env.VITE_CLIENT_REALM;
  const APP_ENV = import.meta.env.VITE_APP_ENV;
  const location = useLocation();
  const { isAllowed: canManage, isLoading: isManageLoading } =
    useAuthAccessQuery({
      relation: 'canManage',
      entityType: 'workspace',
      entityId: workspace.workspaceId
    });
  const { isAllowed: canView, isLoading: isViewLoading } = useAuthAccessQuery({
    relation: 'canView',
    entityType: 'workspace',
    entityId: workspace.workspaceId
  });

  const [isKpiModalOpen, setIsKpiModalOpen] = useState(false);
  const isLoading = isManageLoading || isViewLoading;

  if (isLoading) {
    return <WorkspaceNavLoader size="normal" />;
  }

  if (!canView) {
    return (
      <ButtonText
        isDisabled
        ml={2}
        leftIcon={<FontAwesomeIcon icon={faSliders} />}
      >
        <Text textStyle="textContext">{workspace.name}</Text>
      </ButtonText>
    );
  }

  const isExpanded = !!location.pathname.match(
    new RegExp(`/workspace/${workspace.shortname}/manage/`)
  );

  const activeStyleProp = {
    color: 'actionPrimaryFill',
    fontWeight: 'semibold'
  };
  const menuItems = [
    {
      label: 'Emails',
      path: `workspace/${workspace.shortname}/manage/emails`,
      isVisible: canView
    },
    {
      label: 'Manage Journals',
      path: `workspace/${workspace.shortname}/manage/journals`,
      isVisible: canManage
    },
    {
      label: 'User Management',
      path: `workspace/${workspace.shortname}/manage/user`,
      isVisible: canView
    },
    {
      label: 'Tags Management',
      path: `workspace/${workspace.shortname}/manage/tags`,
      isVisible: canManage
    },
    {
      label: 'Article Types',
      path: `workspace/${workspace.shortname}/manage/article-types`,
      isVisible: canManage
    },
    {
      label: 'Roles',
      path: `workspace/${workspace.shortname}/manage/roles`,
      isVisible: canManage
    },
    {
      label: 'Exports',
      path: `workspace/${workspace.shortname}/manage/vendor-exports`,
      isVisible: canManage
    },
    {
      label: 'Reports',
      path: `workspace/${workspace.shortname}/manage/reports`,
      isVisible: true
    },
    {
      label: 'Analytics',
      path: `workspace/${workspace.shortname}/manage/analytics`,
      isVisible: true
    },
    {
      label: 'KPI',
      path: `${KPI_URL}/${getAppEnv(CLIENT_REALM, APP_ENV)}`,
      isVisible: workspace?.featureConfig?.kpiDashboard,
      isModal: true
    }
  ];

  const activeMenuItem = menuItems.find(({ path, isVisible }) => {
    if (!isVisible) return false;

    return location.pathname.startsWith(`/app/${path}`);
  });

  return (
    <>
      <Menu>
        <MenuButton
          borderWidth="0"
          borderRadius="0"
          variant="outline"
          as={Button}
          color={isExpanded ? 'actionPrimaryFill' : 'none'}
          borderBottom={isExpanded ? '2px solid' : 'none'}
          leftIcon={<FontAwesomeIcon icon={faSliders} />}
          rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
        >
          {activeMenuItem ? activeMenuItem.label : workspace.name}
        </MenuButton>
        <MenuList>
          <MenuGroup title="Manage Workspace">
            {menuItems
              .filter(({ isVisible }) => isVisible)
              .map(item => {
                if ('isModal' in item && item.isModal) {
                  return (
                    <MenuItem
                      key={item.path}
                      onClick={() => setIsKpiModalOpen(true)}
                    >
                      {item.label}
                    </MenuItem>
                  );
                }
                return (
                  <MenuItem
                    key={item.path}
                    to={item.path}
                    as={NavLink}
                    _activeLink={activeStyleProp}
                  >
                    {item.label}
                  </MenuItem>
                );
              })}

            {workspace.featureConfig?.submissionSeeder && (
              <Flex p={1}>
                <SubmissionSeeder workspace={workspace} />
              </Flex>
            )}
          </MenuGroup>
        </MenuList>
      </Menu>
      <KPIRedirectModal
        isOpen={isKpiModalOpen}
        onCancel={() => setIsKpiModalOpen(false)}
        onContinue={() => {
          setIsKpiModalOpen(false);
          window.open(
            `${KPI_URL}/${getAppEnv(CLIENT_REALM, APP_ENV)}`,
            '_blank',
            'noopener,noreferrer'
          );
        }}
      />
    </>
  );
};
